import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React from 'react';

import { AccountTransactionDetailsItem } from '@/components/molecules/AccountTransactionDetailsItem';
import { AmountTransactionDetailsItem } from '@/components/molecules/AmountTransactionDetailsItem';
import { RatesTransactionDetailsItem } from '@/components/molecules/RatesTransactionDetailsItem';
import { DetailsDrawerFees } from '@/components/organisms/DetailsDrawerFees';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { BitsoBank } from '@/types/bitsoBank';
import { PaymentRailType } from '@/types/currency';
import { DetailsField } from '@/types/detailsField';
import { FiatAccountType } from '@/types/fiatAccountType';
import { Locale } from '@/types/locale';
import { FxLockType } from '@/types/simulation';
import {
  FeeType,
  SimplifiedTransactionStatus,
  simplifyTransactionStatus,
  SpreadFee,
  StatusType,
  Transaction,
  TransactionType,
} from '@/types/transaction';
import { formatAmount } from '@/utility/formatAmount';

export const useTransactionDetailsFields = (
  transaction: Transaction,
  fromBanks: readonly BitsoBank[],
  toBanks: readonly BitsoBank[],
  onLinkedTransactionClick?: (transactionId: string) => void,
): ReadonlyArray<DetailsField | null> => {
  const { transactionDetails, date } = transaction;

  const locale = React.useContext<Locale>(LocaleContext);
  const dateFormatter = useDateFormatter('MMM d, yyyy | hh:mm a');
  const tr = useTranslator();
  const percentFormatter = useLocalizedFormatter(PERCENT_FORMATTER_CONFIG);
  const spreadFormatter = useLocalizedFormatter(SPREAD_FORMATTER_CONFIG);
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();
  // TODO: replace this when the endpoint gets fixed
  const fixedDateString = React.useMemo((): string => `${date.replace(' ', 'T')}Z`, [date]);

  return React.useMemo(
    (): ReadonlyArray<DetailsField | null> =>
      transaction
        ? [
            {
              key: 'type',
              label: tr('TRANSACTION_TYPE'),
              value:
                tr(transaction.type) +
                (emptyPaymentTypes.includes(transaction.paymentType)
                  ? ''
                  : `: ${tr(transaction.paymentType)}`),
            },
            {
              key: 'date',
              label: tr('DATE_AND_TIME'),
              value: dateFormatter.parseAndFormat(fixedDateString),
            },
            transaction.type === TransactionType.DEPOSIT &&
            transaction.payInSender?.details?.senderName
              ? {
                  key: 'from',
                  label: tr('FROM'),
                  value: (
                    <Typography variant="body2" color="text.primary">
                      {transaction.payInSender.details.senderName as string}
                    </Typography>
                  ),
                }
              : transaction.fromAccount
                ? {
                    key: 'from',
                    label: tr('FROM'),
                    value: (
                      <AccountTransactionDetailsItem
                        account={transaction.fromAccount}
                        banks={fromBanks}
                      />
                    ),
                  }
                : transaction.type === TransactionType.REFUND
                  ? {
                      key: 'from',
                      label: tr('FROM'),
                      value: (
                        <Typography variant="body2" color="text.primary">
                          Caliza
                        </Typography>
                      ),
                    }
                  : null,
            transaction.toRecipient || transaction.toAccount
              ? {
                  key: 'to',
                  label: tr('TO'),
                  value: (
                    <AccountTransactionDetailsItem
                      account={transaction.toRecipient ?? transaction.toAccount}
                      banks={toBanks}
                    />
                  ),
                }
              : null,
            {
              key: 'amount',
              label: tr('AMOUNT'),
              value: (
                <AmountTransactionDetailsItem
                  from={transaction.fromAmount}
                  to={transaction.toAmount}
                />
              ),
            },
            {
              key: 'fees',
              label: tr('FEES'),
              value: (
                <DetailsDrawerFees
                  totalAmount={transaction.fromAmount}
                  feeDetails={transaction.transactionDetails.feeDetails}
                  fromCurrency={transaction.fromAmount.currencyCode}
                />
              ),
            },
            transaction.type === TransactionType.DEPOSIT &&
            transaction.payInSender?.details?.senderReference
              ? {
                  key: 'senderMemo',
                  label: 'Sender memo',
                  value: transaction.payInSender.details.senderReference as string,
                  columnSpan: 2,
                }
              : null,
            {
              key: 'rates',
              label: tr('CONVERSION_RATES'),
              value: (
                <RatesTransactionDetailsItem
                  from={transaction.fromAmount}
                  to={transaction.toAmount}
                  conversionDetails={transaction.transactionDetails.conversionDetails}
                  spreadFees={transaction.transactionDetails.feeDetails.spreadFees}
                  fxLockSpreadPercentage={transaction.fxLockSpreadPercentage}
                />
              ),
              columnSpan: 2,
            },
            transaction.fxLockType && transaction.fxLockType !== FxLockType.DEFAULT
              ? {
                  key: 'fxRateLock',
                  label: tr('EXCHANGE_RATE_LOCK'),
                  value: (
                    <Box>
                      <Typography variant="body2" color="text.primary">
                        {tr(
                          'FX_LOCK_DURATION',
                          String(FX_LOCK_DURATION_HOURS[transaction.fxLockType]),
                        )}
                      </Typography>
                      <Typography variant="body2" color="custom.LightGray">
                        {transaction.fxLockSpreadPercentage != null &&
                        transaction.fxLockSpreadPercentage > 0
                          ? tr(
                              'FX_LOCK_SPREAD',
                              spreadFormatter.format(transaction.fxLockSpreadPercentage),
                            )
                          : tr('FX_LOCK_NO_EXTRA_SPREAD')}
                      </Typography>
                    </Box>
                  ),
                }
              : null,
            getRevenueField(
              transactionDetails.feeDetails.spreadFees,
              locale,
              percentFormatter,
              tr,
              displayUsdcAsUsd,
              transaction.status,
              transaction.completed,
            ),
            transactionDetails.paymentReference
              ? {
                  key: 'paymentReference',
                  label: tr('PAYMENT_REFERENCE_DETAILS_LABEL'),
                  value: transactionDetails.paymentReference,
                  title: transactionDetails.paymentReference,
                  columnSpan: 2,
                }
              : null,
            {
              key: 'id',
              label: tr('TRANSACTION_ID'),
              value: transaction.id,
              title: transaction.id,
              columnSpan: 2,
            },
            transaction.refundTransactionId
              ? {
                  key: 'refundTransactionId',
                  label: tr('REFUND_TRANSACTION'),
                  value: onLinkedTransactionClick ? (
                    <Typography variant="body2" color="text.primary" component="span">
                      <Link
                        component="button"
                        type="button"
                        underline="always"
                        sx={styles.linkedTransactionId}
                        onClick={(): void =>
                          onLinkedTransactionClick(transaction.refundTransactionId as string)
                        }>
                        {transaction.refundTransactionId}
                      </Link>
                    </Typography>
                  ) : (
                    transaction.refundTransactionId
                  ),
                  title: transaction.refundTransactionId,
                  columnSpan: 2,
                }
              : null,
            transaction.originalTransactionId
              ? {
                  key: 'originalTransactionId',
                  label: tr('ORIGINAL_TRANSACTION'),
                  value: onLinkedTransactionClick ? (
                    <Typography variant="body2" color="text.primary" component="span">
                      <Link
                        component="button"
                        type="button"
                        underline="always"
                        sx={styles.linkedTransactionId}
                        onClick={(): void =>
                          onLinkedTransactionClick(transaction.originalTransactionId as string)
                        }>
                        {transaction.originalTransactionId}
                      </Link>
                    </Typography>
                  ) : (
                    transaction.originalTransactionId
                  ),
                  title: transaction.originalTransactionId,
                  columnSpan: 2,
                }
              : null,
          ]
        : [],
    [
      dateFormatter,
      displayUsdcAsUsd,
      fixedDateString,
      fromBanks,
      locale,
      onLinkedTransactionClick,
      percentFormatter,
      spreadFormatter,
      toBanks,
      tr,
      transaction,
      transactionDetails.feeDetails.spreadFees,
      transactionDetails.paymentReference,
    ],
  );
};

const styles = {
  linkedTransactionId: {
    p: 0,
    border: 0,
    background: 'transparent',
    font: 'inherit',
    color: 'inherit',
    verticalAlign: 'baseline',
    textAlign: 'left',
    cursor: 'pointer',
  },
};

const emptyPaymentTypes = [FiatAccountType.unset, PaymentRailType.LocalExchange];

const FX_LOCK_DURATION_HOURS: Record<FxLockType, number> = {
  [FxLockType.DEFAULT]: 2,
  [FxLockType.FX_LOCK_5]: 5,
  [FxLockType.FX_LOCK_24]: 24,
  [FxLockType.FX_LOCK_48]: 48,
  [FxLockType.FX_LOCK_72]: 72,
};

const PERCENT_FORMATTER_CONFIG = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  style: 'percent' as const,
};

const SPREAD_FORMATTER_CONFIG = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

const getRevenueField = (
  spreadFees: ReadonlyArray<SpreadFee>,
  locale: Locale,
  percentFormatter: Intl.NumberFormat,
  tr: (key: string, ...args: string[]) => string,
  displayUsdcAsUsd: boolean,
  status: StatusType,
  completed: boolean,
): DetailsField | null => {
  const simplified = simplifyTransactionStatus(status, completed);
  if (
    simplified === SimplifiedTransactionStatus.FAILED ||
    simplified === SimplifiedTransactionStatus.EXPIRED
  ) {
    return null;
  }

  const integratorSpread = spreadFees.find(
    (fee: SpreadFee): boolean => fee.type === FeeType.INTEGRATOR_SPREAD,
  );

  if (!integratorSpread) {
    return null;
  }

  const amount = formatAmount(
    locale,
    Number(integratorSpread.calculatedFee),
    integratorSpread.feeCurrency,
    undefined,
    undefined,
    displayUsdcAsUsd,
  );
  const percentage = percentFormatter.format(Number(integratorSpread.spreadBasisPoint));

  return {
    key: 'revenue',
    label: tr('YOUR_EARNINGS'),
    value: (
      <Box>
        <Typography variant="body2" color="text.primary">
          {amount}
        </Typography>
        <Typography variant="body2" color="custom.LightGray">
          {percentage} spread
        </Typography>
      </Box>
    ),
  };
};
